// Import module-specific constants
import { CUSTOMER_PROFILES_HEADING } from '@/lib/customer-profiles/constants';
import { UI_TEXT } from '@/lib/cms/constants';

export const SSO_LOGIN_TEXT = {
    TITLE: "Sign in",
    DESCRIPTION:
        "Sign in to access your admin dashboard",
    BUTTON_LABEL: "Sign in with SSO",
    FOOTER_TEXT: "Having an issue?",
    FOOTER_LINK_TEXT: "Contact your admin",
    SUPPORT_LINK: "/support",
    LOGO_SRC: "/images/westay-black.svg",
    LOGO_ALT: "Westay logo",
    SECURE_AUTH_TEXT: "Secure authentication",
    MS_ACCOUNT_REQUIRED: {
        TITLE: "Microsoft Account Required",
        DESCRIPTION:
            "Use your organization's Microsoft account to access the admin system securely.",
    },
    FOOTER_SECURITY: "Protected by enterprise-grade security",
    ENCRYPTION_LABELS: {
        SSL: "SSL Encrypted",
        OAUTH: "OAuth 2.0",
    },
};

export const HEADER_CONSTANTS = {
    DEFAULT_ROUTE: "Dashboard",
    BREADCRUMB_HOME: "Home",
    BREADCRUMB_HOME_HREF: "/admin/dashboard",
    LOADING_TEXT: "Loading...",
    HEADING_LABEL: "Dashboard",
    HEADING_TEXT: "Business performance overview",
    FALLBACK_INITIAL: "NA",
    MENU: {
        SETTINGS: "Settings",
        SIGN_OUT: "Sign Out"
    },
    ROUTES: {
        SETTINGS: "/settings"
    }
};

// Page Headings Configuration
export const PAGE_HEADINGS = {
    GDS_RATE_CODES: {
        title: "GDS Rate Codes",
        description: "Manage negotiated rates and preferred partnerships"
    },
    DASHBOARD: {
        title: HEADER_CONSTANTS.HEADING_LABEL,
        description: HEADER_CONSTANTS.HEADING_TEXT
    },
    CUSTOMER_PROFILES: {
        title: CUSTOMER_PROFILES_HEADING.label,
        description: CUSTOMER_PROFILES_HEADING.text
    },
    CMS_PROMOTIONS: {
        title: UI_TEXT.CMS_PAGE_TITLE,
        description: UI_TEXT.CMS_PAGE_SUBTITLE
    },
    HOTELS: {
        title: "Hotels",
        description: "Manage hotel properties and details"
    },
    BOOKING_QUEUE: {
        title: "Booking Queue",
        description: "Manage Expedia B2B bookings awaiting confirmation"
    },
    BOOKINGS: {
        title: "Bookings",
        description: "View and manage all bookings"
    },
    PAYMENTS: {
        title: "Payments",
        description: "Track and manage payment transactions"
    },
    REPORTS: {
        title: "Reports",
        description: "View analytics and generate reports"
    },
    SETTINGS: {
        title: "Settings",
        description: "Configure system settings and preferences"
    },
    ROOMS: {
        title: "Rooms",
        description: "Manage room types and availability"
    },
    RATE_PLANS: {
        title: "Rate Plans",
        description: "Configure pricing and rate strategies"
    },
    LOYALTY_WALLET: {
        title: "Loyalty Wallet",
        description: "Manage customer loyalty programs"
    },
    PROGRAMS: {
        title: "GDS Rate Code Management",
        description: "Manage negotiated rates and preferred partnerships"
    },
    REVIEW_MANAGEMENT: {
        title: "Review Management",
        description: "Monitor and respond to customer reviews"
    },
    WESTAY_USERS: {
        title: "Westay Users",
        description: "Manage system users and permissions"
    },
    SUPPLIER_CREDIT: {
        title: "Supplier Credit",
        description: "Track supplier credits and transactions"
    },
    NOTIFICATIONS: {
        title: "Notifications",
        description: "View and manage system notifications"
    },
    CLIENT_PROFILES: {
        title: "Client Profiles",
        description: "Manage client accounts and information"
    },
    SUPPORT_CHART: {
        title: "Support Chart",
        description: "View support tickets and analytics"
    },
    MARKUP_MANAGEMENT:{
         title: "Markup Management",
        description: "Configure and manage markup rules for suppliers, price ranges, and locations"
    },
    JET_REQUESTS: {
        title: "Jet Requests",
        description: "Manage private jet requests and inquiries from customers"
    },
    YACHT_REQUESTS: {
        title: "Yacht Requests",
        description: "Manage yacht charter requests and inquiries from customers"
    },
    HOTEL_AMENDMENTS: {
        title: "Hotel Amendments",
        description: "Manage hotel booking modification requests"
    },
    CANCELLATION_QUEUES: {
        title: "Cancel / Refund Queues",
        description: "Manage all cancellation requests across Hotel and On Request bookings"
    },
    BOOKING_FAILURE_QUEUES: {
        title: "Booking Failure Queues",
        description: "Manage failed bookings"
    },
    RULE_MASTER: {
        title: "Rule Master",
        description: "Manage comparison, discount, and markup rules in one place"
    }
};

export const PAYMENTS_PORTAL_CONSTANTS = {
    TITLE: "Westay Payments Portal",
    DESCRIPTION: "Access secure transaction management, refund queues, and paydash services via our unified financial dashboard.",
    SECURE_GATEWAY_TITLE: "Secure Gateway",
    SECURE_GATEWAY_DESC: "Fully encrypted operations powered by Westay PayDash.",
    WORKSPACE_TITLE: "Independent Workspace",
    WORKSPACE_DESC: "Opens in a secure workspace tab to keep your dashboard session uninterrupted.",
    LAUNCH_BUTTON: "Launch Payments Portal",
    FALLBACK_FOOTER_PREFIX: "Clicking the button above opens ",
    FALLBACK_FOOTER_SUFFIX: " in a secure new tab.",
    PORTAL_URL: "https://paydashdev.westay.com/"
};

export const PROGRAMS_MANAGEMENT_TEXT = {
  PAGE_TITLE: "GDS Rate Code Management",
  PAGE_SUBTITLE: "Manage negotiated rates and preferred partnerships",
  NEW_PROGRAM_BTN: "New Program",
  BUTTONS: {
    PREVIOUS: "Previous",
    NEXT: "Next"
  },
  SEARCH_PLACEHOLDER: "Search programs...",
  ALL_TYPES: "All Types",
  SEARCH_BTN: "Search",
  MORE_FILTERS_BTN: "More Filters",
  SUMMARY: {
    ACTIVE: "active",
    INACTIVE: "inactive"
  },
  SHOWING_TEXT: (count: number, total: number) => `Showing ${count} of ${total} programs`,
  EMPTY_STATE: {
    TITLE: "No GDS Programs Found",
    DESC: "You haven't set up any GDS codes or negotiated rates yet. Create your first program to start managing exclusive rate codes, tracking participating hotels, and organizing agency commissions.",
    CREATE_BTN: "Create First Program",
    SEARCH_PROMPT_TITLE: "Search Programs",
    SEARCH_PROMPT_DESC: "Use the search bar and click Search to find GDS rate programs.",
    NO_RESULTS_TITLE: "No results found",
    NO_RESULTS_DESC: "We couldn't find any GDS rate programs matching your search criteria or filters. Try adjusting them and search again.",
    NO_MORE_RESULTS: "No more programs to load"
  },
  FILTERS: {
    RESET_BTN: "Reset",
    ACTIVE_FILTERS: "Active Filters:",
    CLEAR_ALL: "Clear All"
  },
  CARD: {
    CHAINS: "Chains",
    HOTELS: "Hotels",
    COMMISSION: "Commission",
    PARTNER_CODE_LABEL: "GDS Rate Code",
    PARTNER_CODE_DESC: "Use this code for exclusive hotel bookings and preferred rates",
    VIEW_BTN: "View",
    EDIT_BTN: "Edit Program",
    ACTIVATE: "Activate",
    DEACTIVATE: "Deactivate"
  },
  MODAL: {
    TITLES: {
      FILTERS: "Advanced Filters",
      CREATE: "Create New Program",
      EDIT: "Edit Program",
    },
    DESCRIPTIONS: {
      FILTERS: "Narrow down programs by status, chain ID, hotel ID, supplier, and commission.",
      VIEW: "View program details and assigned properties",
      CREATE: "Set up a new partnership program",
      EDIT: "Modify existing program details"
    },
    TABS: {
      OVERVIEW: "Overview"
    },
    INFO: {
      TITLE: "Program Information",
      ID: "Program ID:",
      SCOPE: "Scope:",
      TYPE: "Type:",
      COMMISSION: "Commission:"
    },
    STATUS: {
      TITLE: "Program Status",
      LABEL: "Status:"
    },
    AUDIT: {
      TITLE: "Change History",
      CREATED_BY: "Created By:",
      DATE: "Date:",
      MODIFIED_BY: "Last Modified By:",
      CHANGED_BY: "Changed by:",
      EVENTS_TEXT: (count: number) => `${count} ${count === 1 ? 'event' : 'events'}`,
      ACTION_CREATED: "CREATED",
      ACTION_UPDATED: "UPDATED"
    },
    CODE_SECTION: {
      TITLE: "GDS Rate Code",
      DESC: "Use this code for exclusive hotel bookings and preferred rates. Share with qualified travel advisors only."
    },
    PROPERTIES: {
      TITLE_HOTELS: "Participating Hotels",
      TITLE_CHAINS: "Participating Chains",
      EMPTY: "No properties assigned to this program yet."
    },
    FORM: {
      VALIDATION_ALERT: "Please fill in all mandatory fields before saving.",
      NAME_LABEL: "Program Name *",
      NAME_PLACEHOLDER: "Enter program name",
      TYPE_LABEL: "Program Type *",
      TYPE_PLACEHOLDER: "Select type",
      SCOPE_LABEL: "Program Scope *",
      SCOPE_CHAIN: "Chain Group",
      SCOPE_CHAIN_REQ: "Chain Group *",
      SCOPE_HOTEL: "Hotel",
      SCOPE_HOTEL_REQ: "Hotel *",
      SEARCH_CHAIN_PLACEHOLDER: "Search and add chain groups...",
      SEARCH_HOTEL_PLACEHOLDER: "Search and add hotels by name, hotel ID, or location...",
      SEARCH_CHAIN_DESC: "Add multiple chains - search by chain name or Chain ID.",
      SEARCH_HOTEL_DESC: "Add multiple hotels at once - search by hotel name, Hotel ID, or location.",
      COMMISSION_LABEL: "Commission Rate",
      CODE_LABEL: "GDS Rate Code *",
      CODE_PLACEHOLDER: "e.g. XYZ",
      CODE_DESC: "The code partners use for exclusive bookings and preferred rates. (Must be exactly 3 characters)",
      SUPPLIER_LABEL: "Supplier *",
      SUPPLIER_PLACEHOLDER: "Select a supplier",
      SUPPLIER_DESC: "The GDS / distribution supplier this program's rates are available through.",
      COMMISSION_ERROR: "Must be between 0 and 100"
    },
    FILTERS: {
      STATUS: "Program Status",
      STATUS_ALL: "All",
      STATUS_ACTIVE: "Active",
      STATUS_INACTIVE: "Inactive",
      CHAIN_ID: "Chain Group",
      CHAIN_PLACEHOLDER: "Search chain ID or name...",
      CHAIN_DESC: "Only programs linked to the selected chain ID are shown",
      HOTEL_ID: "Hotel",
      HOTEL_PLACEHOLDER: "Search hotel ID or name...",
      HOTEL_DESC: "Only programs that include the selected hotel are shown",
      COMMISSION: "Commission Rate (%)",
      COMMISSION_MIN: "Minimum",
      COMMISSION_MAX: "Maximum",
      SUPPLIER: "Supplier",
      SUPPLIER_PLACEHOLDER: "Select Supplier",
      SUPPLIER_DESC: "Only programs available through the selected GDS / distribution supplier are shown",
      ACTIVE_FILTERS: "Active Filters",
      ACTIVE_MATCH: (count: number, total: number) => `${count} of ${total} programs match your current criteria`
    },
    FOOTER: {
      CLEAR_FILTERS: "Clear All Filters",
      CANCEL: "Cancel",
      APPLY_FILTERS: "Apply Filters",
      CLOSE: "Close",
      SAVE_CREATE: "Create Program",
      SAVE_EDIT: "Save Changes"
    }
  },
  ERRORS: {
    DUPLICATE_PLURAL: (targetType: string) => `One or more of the selected ${targetType}s are already configured for this supplier. Please try different ones.`,
    DUPLICATE_SINGULAR: (targetType: string) => `The selected ${targetType} is already configured for this supplier. Please try a different one.`
  },
  CONSTANTS: {
    STATUS_ACTIVE: 'ACTIVE',
    STATUS_INACTIVE: 'INACTIVE',
    VARIANT_SUCCESS: 'success',
    VARIANT_NEUTRAL: 'neutral',
    VARIANT_INFO: 'info',
    VARIANT_WARNING: 'warning',
    VARIANT_DANGER: 'danger',
    SCOPE_CHAIN: 'chain',
    SCOPE_HOTEL: 'hotel',
    SCOPE_API_CHAIN: 'CHAIN',
    SCOPE_API_HOTEL: 'HOTEL',
    TARGET_CHAIN_GROUP: 'chain group',
    TARGET_HOTEL: 'hotel',
    TYPE_PREFERRED: 'preferred',
    TYPE_ELITE: 'elite',
    TYPE_NEGOTIATED: 'negotiated',
    ERROR_MATCH_DUPLICATE: 'already configured',
    PLURAL_THRESHOLD: 1,
    MODAL_TYPE_CREATE: 'create',
    MODAL_TYPE_EDIT: 'edit',
    MODAL_TYPE_VIEW: 'view',
    MODAL_TYPE_FILTERS: 'filters',
    MIN_LENGTH: 3,
    EMPTY_LENGTH: 0,
    MIN_COMMISSION: 0,
    MAX_COMMISSION: 100,
    MIN_CHARS_MSG: "Min 3 characters",
    EXACT_3_CHARS_MSG: "Code must be exactly 3 characters",
    ACTIVE_FILTER_COUNT: 1,
    INACTIVE_FILTER_COUNT: 0,
    INITIAL_COUNT: 0,
    SINGLE_ITEM_COUNT: 1,
    INITIAL_PAGE: 1,
    PAGE_LIMIT: 6,
    SUPPLIER_EXPEDIA: 'EXPEDIA'
  }
};








export const PROPERTY_SELECTOR_TEXT = {
  TAB_CHAIN: "Chain",
  TAB_HOTEL: "Hotel",
  WARNING_CLEAR_CHAIN: "To add hotels, remove the selected chain(s) first.",
  WARNING_CLEAR_HOTEL: "To add chains, remove the selected hotel(s) first."
};
